import React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import type { PublicSupplierProfileVO } from '../../../types/model/valueObject/account/PublicSupplierProfileVO';

interface TrustCard {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

interface SupplierTrustStripProps {
  profile: PublicSupplierProfileVO;
}

const ICON_BADGE_SX = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  bgcolor: 'rgba(63,81,181,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

const ICON_SX = { fontSize: '1.1rem', color: 'primary.main' } as const;

const SupplierTrustStrip = ({ profile }: SupplierTrustStripProps) => {
  const { yearFounded, locations } = profile;
  const hasInstantApproval = locations.some(l => l.hasInstantApproval);

  const cards: TrustCard[] = [];

  if (profile.verified) {
    cards.push({
      icon: <VerifiedOutlinedIcon sx={ICON_SX} />,
      title: 'Verified supplier',
      subtitle: 'Identity confirmed',
    });
  }

  if (yearFounded) {
    const yearsAgo = new Date().getFullYear() - yearFounded;
    if (yearsAgo > 0) cards.push({
      icon: <CalendarTodayOutlinedIcon sx={ICON_SX} />,
      title: `Since ${yearFounded}`,
      subtitle: `${yearsAgo} year${yearsAgo !== 1 ? 's' : ''} on SecurSpace`,
    });
  }

  if (locations.length > 0) {
    const stateCount = new Set(locations.map(l => l.state).filter(Boolean)).size;
    const stateLabel = stateCount > 1 ? `Across ${stateCount} states` : `Across ${stateCount} state`;

    cards.push({
      icon: <RoomOutlinedIcon sx={ICON_SX} />,
      title: `${locations.length} location${locations.length !== 1 ? 's' : ''}`,
      subtitle: stateLabel,
    });
  }

  if (hasInstantApproval) {
    cards.push({
      icon: <BoltOutlinedIcon sx={ICON_SX} />,
      title: 'Instant approval',
      subtitle: 'Available spaces open now',
    });
  }

  if (cards.length === 0) return null;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid item xs={6} md={3} key={card.title}>
          <Box
            sx={{
              border: '1px solid #E9E9FB',
              borderRadius: 2,
              p: '12px 16px',
              bgcolor: '#fff',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={ICON_BADGE_SX}>{card.icon}</Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Satoshi-Variable, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  {card.subtitle}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default SupplierTrustStrip;
